import { useState } from "react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    inquiryType: "",
    hearAboutUs: "",
    message: "",
    agreeToTerms: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    // Add your form submission logic here
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  return (
    <div className="bg-white py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-16 xl:px-20 font-montserrat">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="mb-12 lg:mb-16">
          <div className="max-w-4xl relative">
            {/* Abstract Design */}

            <h1 className="text-primary text-2xl md:text-3xl lg:text-4xl font-semibold leading-[150%] mb-3">
              Let's Connect
            </h1>
            <p className="text-slate-600 text-sm md:text-base font-medium leading-[150%]">
              We're excited to connect with you and learn more about your real
              estate goals. Use the form below to get in touch with Alkina Realty.
              Whether you're a prospective client or partner, we're here to help.
            </p>
          </div>
        </div>

        {/* Form Section */}
        <form
          onSubmit={handleSubmit}
          className="border border-gray-200 p-6 md:p-8 lg:p-10 bg-white shadow-lg"
        >
          <div className="space-y-8 lg:space-y-10">
            {/* First Row - First Name, Last Name, Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* First Name */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter First Name"
                  className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
                />
              </div>

              {/* Last Name */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter Last Name"
                  className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your Email"
                  className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
                />
              </div>
            </div>

            {/* Second Row - Phone, Inquiry Type, How Did You Hear */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {/* Phone */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter Phone Number"
                  className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
                />
              </div>

              {/* Inquiry Type */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  Inquiry Type
                </label>
                <div className="relative">
                  <select
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 appearance-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors cursor-pointer"
                  >
                    <option value="">Select Inquiry Type</option>
                    <option value="general">General Inquiry</option>
                    <option value="buying">Buying Property</option>
                    <option value="selling">Selling Property</option>
                    <option value="renting">Renting</option>
                    <option value="other">Other</option>
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.5 8.25L12 15.75L4.5 8.25"
                      stroke="#64748b"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* How Did You Hear About Us */}
              <div className="flex flex-col gap-2">
                <label className="text-slate-900 text-base font-semibold leading-[150%]">
                  How Did You Hear About Us?
                </label>
                <div className="relative">
                  <select
                    name="hearAboutUs"
                    value={formData.hearAboutUs}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 appearance-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors cursor-pointer"
                  >
                    <option value="">Select</option>
                    <option value="search">Search Engine</option>
                    <option value="social">Social Media</option>
                    <option value="referral">Referral</option>
                    <option value="advertisement">Advertisement</option>
                    <option value="other">Other</option>
                  </select>
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.5 8.25L12 15.75L4.5 8.25"
                      stroke="#64748b"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="flex flex-col gap-2">
              <label className="text-slate-900 text-base font-semibold leading-[150%]">
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your Message here.."
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-200 text-slate-700 text-sm font-medium leading-5 placeholder:text-slate-400 resize-none focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-colors"
              />
            </div>

            {/* Bottom Row - Checkbox and Button */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              {/* Checkbox */}
              <div className="flex items-start sm:items-center gap-2 flex-1">
                <input
                  type="checkbox"
                  name="agreeToTerms"
                  id="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="w-5 h-5 mt-0.5 sm:mt-0 bg-white border border-gray-200 accent-blue-600 cursor-pointer flex-shrink-0"
                />
                <label
                  htmlFor="agreeToTerms"
                  className="text-slate-600 text-sm font-medium leading-[150%] cursor-pointer"
                >
                  I agree with{" "}
                  <span className="underline">Terms of Use</span> and{" "}
                  <span className="underline">Privacy Policy</span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="bg-primary hover:bg-primary/90 transition-colors px-8 py-3 text-white text-sm font-medium leading-6 whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                Send Your Message
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

